import React, {
    ReactElement, useCallback, useState,
} from "react";
import { AiOutlinePlus } from "react-icons/ai";
import { BsTrash } from "react-icons/bs";
import { Loading } from "..";
import useRouting, { Link, Paths, useParams } from "../../contexts/Routing";
import { ErrorRemote } from "../../pages/error/Error";
import {
    GetUsersDocument, useDeleteUserMutation, useGetOrganizationsQuery, useGetUsersQuery,
} from "../../__generated__/api";
import Avatar from "../avatar/Avatar";
import Confirm from "../confirm/Confirm";
import ScrollableList from "../scrollable-list/ScrollableList";

export function UserList({
    companyId,
    shouldAllowTabs,
}: {
    companyId: string;
    shouldAllowTabs?: boolean;
}): ReactElement {
    const { historyPush } = useRouting();
    const [userSearch, setUserSearch] = useState<string | undefined>(undefined);

    const [deleteUser] = useDeleteUserMutation();
    const [userToDelete, setUserToDelete] = useState<{ id: string; name: string; organizationName: string }>();

    const { data: organization, loading: isOrganizationLoading } = useGetOrganizationsQuery({
        skip: companyId == null,
        variables: {
            companyIds: [companyId],
        },
    });

    const {
        data, loading: isLoading, fetchMore, error,
    } = useGetUsersQuery({
        variables: {
            query: userSearch,
            companyId,
        },
    });

    const handleScroll = useCallback(() => {
        if (data?.guestUsersByCompany?.nextPageToken) {
            fetchMore({
                variables: {
                    nextPageToken: data?.guestUsersByCompany.nextPageToken,
                },
                updateQuery(previous, { fetchMoreResult }) {
                    return {
                        guestUsersByCompany: {
                            id: fetchMoreResult.guestUsersByCompany?.nextPageToken,
                            count: (previous.guestUsersByCompany?.count ?? 0) + (fetchMoreResult.guestUsersByCompany?.count ?? 0),
                            results: previous.guestUsersByCompany?.results.concat(
                                fetchMoreResult.guestUsersByCompany?.results ?? [],
                            ) ?? [],
                            nextPageToken: fetchMoreResult.guestUsersByCompany?.nextPageToken,
                        },
                        __typename: fetchMoreResult.__typename,
                    };
                },
            });
        }
    }, [data?.guestUsersByCompany?.nextPageToken]);

    if ((isLoading || isOrganizationLoading) && !userSearch) {
        return <Loading />;
    }

    if (error) {
        return <ErrorRemote error={error} />;
    }

    const users = data?.guestUsersByCompany?.results ?? [];
    const organizationName = organization?.companies.results.firstElement.name;

    const handleDeleteUser = async (id: string) => {
        await deleteUser({
            refetchQueries: [
                { query: GetUsersDocument },
            ],
            variables: {
                id,
            },
        });

        setUserToDelete(undefined);
    };

    return (
        <div className="border p-3 rounded bg-white">
            <Confirm
                confirmText="Delete"
                onConfirm={() => {
                    if (!userToDelete) {
                        return;
                    }

                    handleDeleteUser(userToDelete.id);
                }}
                shouldShow={userToDelete != null}
                closeText="Close"
                onHide={() => setUserToDelete(undefined)}
            >
                <div>
                    Are you sure you want to remove this user?
                    {" "}
                    <div className="mt-2 p-2 rounded alert alert-danger">
                        {userToDelete?.name}
                        {" "}
                        (
                        {userToDelete?.organizationName}
                        )
                    </div>
                </div>
            </Confirm>
            {users?.isNotEmpty || (userSearch !== "" && userSearch) ? (
                <div>
                    {shouldAllowTabs && organizationName && companyId && (
                        <ul className="nav nav-pills mb-4">
                            <li className="nav-item">
                                <div className="nav-link cursor-pointer text-reset text-gray-600
                                            list-border-primary list-border border-start shadow"
                                >
                                    {organizationName}
                                </div>
                            </li>
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className="nav-link cursor-pointer text-reset text-gray-600"
                                    onClick={() => historyPush(Paths.CONTACTS_ENTITY, { entity: "organizations" })}
                                >
                                    All Organizations
                                </button>
                            </li>
                        </ul>
                    )}
                    <div className="d-flex mb-3">
                        <input
                            className="no-border flex-grow-1 mx-2 w-100"
                            type="search"
                            placeholder={shouldAllowTabs ? `Search in ${(organizationName) || "users"}...` : "Search..."}
                            value={userSearch}
                            onChange={evt => {
                                setUserSearch(evt.target.value);
                            }}
                        />
                        <div className="d-flex me-2 ms-4">
                            <Link
                                to={Paths.USERS_NEW}
                                search={{ companyId }}
                                className="btn btn-none btn-sm"
                            >
                                <AiOutlinePlus />
                                {" "}
                                User
                            </Link>
                        </div>
                    </div>
                    <div className="list-group">
                        {users.isEmpty && userSearch ? (
                            <div>
                                {isLoading ? <Loading /> : "No results found"}
                            </div>
                        ) : (
                            <ScrollableList
                                onScroll={handleScroll}
                            >
                                {users?.map(user => (

                                    <div
                                        className="d-flex w-100 justify-content-between list-group-item align-items-center"
                                        key={user.id}
                                    >
                                        <div className="d-flex flex-grow-1 align-items-center">
                                            <Link
                                                className="d-flex w-100 bg-none justify-content-start align-items-center
                                            border-0 btn btn-none p-0"
                                                to={Paths.USERS_ID}
                                                params={{
                                                    id: user.id,
                                                }}
                                            >
                                                <Avatar
                                                    src={user.avatarUrl as string}
                                                    isCircle
                                                    variant="xs"
                                                />
                                                <div>
                                                    {user.name}
                                                </div>
                                                {!companyId
                                                    && (<small className="text-light ms-3">{user?.organization?.name}</small>)}
                                            </Link>
                                        </div>
                                        <div className="ms-2 text-light">
                                            <button
                                                className="btn btn-none text-gray-500 p-0"
                                                type="button"
                                                onClick={() => setUserToDelete({
                                                    id: user.id,
                                                    name: user.name ?? "",
                                                    organizationName: user.organization?.name ?? "",
                                                })}
                                            >
                                                <BsTrash />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </ScrollableList>
                        )}
                        <div className="d-flex justify-content-end align-items-center mt-3">

                            <Link
                                to={Paths.USERS_NEW}
                                search={{ companyId }}
                                className="btn btn-secondary btn-sm"
                            >
                                <AiOutlinePlus />
                                {" "}
                                User
                            </Link>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="d-flex flex-column align-items-center justify-content-center text-light my-4">
                    <h5 className="text-light">
                        Users
                    </h5>
                    <Link
                        to={Paths.USERS_NEW}
                        search={{ companyId }}
                        className="btn btn-secondary btn-sm my-3"
                    >
                        <AiOutlinePlus />
                        {" "}
                        Add User
                    </Link>
                    {!companyId && !organizationName ? (
                        <small>
                            No users. Click Add to create a new user.
                        </small>
                    ) : (
                        <small>
                            No users available in
                            {" "}
                            {organizationName}
                            . Click Add to create a new user.
                        </small>
                    )}
                </div>
            )}
        </div>
    );
}

UserList.defaultProps = {
    shouldAllowTabs: false,
};

export default function User() {
    const { companyId } = useParams(Paths.ORGANIZATION_USERS);
    return <UserList companyId={companyId} shouldAllowTabs />;
}
